import React, { useState, useEffect } from 'react';
import EventActionsConsole from '../../components/actions/EventActionsConsole';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Modal from '../../components/ui/Modal';
import {
  Phone,
  AlertTriangle,
  Volume2,
  DoorOpen,
  Bell,
  FileText,
  Loader2,
  MapPin,
  Wifi,
  WifiOff,
  CheckCircle,
  Clock,
  XCircle,
  Activity,
  Unlock,
  Shield,
} from 'lucide-react';
import { locationActionService } from '../../services';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import type { LocationAction, LocationActionType } from '../../types';

// Icon and styling configuration for action types
const ACTION_TYPE_CONFIG: Record<LocationActionType, { 
  icon: React.ReactNode; 
  iconLarge: React.ReactNode;
  bgColor: string;
  hoverColor: string;
  label: string;
  buttonText: string;
  isEmergency?: boolean;
}> = {
  GATE_CONTROLLER: { 
    icon: <DoorOpen size={20} />, 
    iconLarge: <Unlock size={32} />,
    bgColor: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    label: 'Gate Controller',
    buttonText: 'Open Gate',
  },
  SIREN: { 
    icon: <Volume2 size={20} />, 
    iconLarge: <Volume2 size={32} />,
    bgColor: 'bg-orange-500',
    hoverColor: 'hover:bg-orange-600',
    label: 'Siren',
    buttonText: 'Trigger Alarm',
  },
  CALL_911: { 
    icon: <AlertTriangle size={20} />, 
    iconLarge: <AlertTriangle size={40} />,
    bgColor: 'bg-red-600',
    hoverColor: 'hover:bg-red-700',
    label: 'Emergency 911',
    buttonText: 'CALL 911',
    isEmergency: true,
  },
  CALL_SECURITY: { 
    icon: <Phone size={20} />, 
    iconLarge: <Shield size={32} />,
    bgColor: 'bg-green-600',
    hoverColor: 'hover:bg-green-700',
    label: 'Security',
    buttonText: 'Call Security',
  },
  CALL_RESIDENT: { 
    icon: <Phone size={20} />, 
    iconLarge: <Phone size={32} />,
    bgColor: 'bg-indigo-600',
    hoverColor: 'hover:bg-indigo-700',
    label: 'Resident',
    buttonText: 'Call Resident',
  },
  INTERCOM: { 
    icon: <Bell size={20} />, 
    iconLarge: <Bell size={32} />,
    bgColor: 'bg-purple-500',
    hoverColor: 'hover:bg-purple-600',
    label: 'Intercom',
    buttonText: 'Activate Intercom',
  },
};

const ActionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId: globalLocationId, selectedLocation, isLoading: isLoadingLocations } = useLocationContext();
  const selectedLocationId = globalLocationId === 'all' ? null : Number(globalLocationId);
  const [configuredActions, setConfiguredActions] = useState<LocationAction[]>([]);
  const [isLoadingActions, setIsLoadingActions] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Action execution state
  const [triggeringActionId, setTriggeringActionId] = useState<number | null>(null);
  const [testingActionId, setTestingActionId] = useState<number | null>(null);
  const [actionStatuses, setActionStatuses] = useState<Record<number, { online: boolean; lastChecked: Date }>>({});
  
  // Confirmation modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState<LocationAction | null>(null);
  
  // Dial info modal for call actions
  const [dialInfo, setDialInfo] = useState<{ phoneNumber: string; callerId: string; sipUri?: string; actionName: string } | null>(null);
  
  // Activity log
  const [activityLog, setActivityLog] = useState<Array<{ id: number; message: string; time: Date; type: 'success' | 'error' | 'info' }>>([]);

  // Follow the header selection and ignore responses for a previous location.
  useEffect(() => {
    let cancelled = false;
    setConfiguredActions([]);
    setActionStatuses({});
    setError(null);
    setConfirmModalOpen(false);
    setActionToConfirm(null);
    setDialInfo(null);
    if (!selectedLocationId) {
      setIsLoadingActions(false);
      return;
    }
    setIsLoadingActions(true);
    locationActionService.getLocationActions(selectedLocationId, { include_inactive: false })
      .then(actions => {
        if (!cancelled) setConfiguredActions(Array.isArray(actions) ? actions : []);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to fetch actions');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingActions(false);
      });
    return () => { cancelled = true; };
  }, [selectedLocationId]);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Add to activity log
  const addActivity = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setActivityLog(prev => [
      { id: Date.now(), message, time: new Date(), type },
      ...prev.slice(0, 9)
    ]);
  };

  // Test device connectivity
  const handleTest = async (action: LocationAction) => {
    try {
      setTestingActionId(action.id);
      const result = await locationActionService.testAction(action.id);
      setActionStatuses(prev => ({
        ...prev,
        [action.id]: { online: result.reachable, lastChecked: new Date() }
      }));
      if (result.reachable) {
        addActivity(`${action.name} - Online`, 'success');
      } else {
        addActivity(`${action.name} - Offline`, 'error');
      }
    } catch (err) {
      setActionStatuses(prev => ({
        ...prev,
        [action.id]: { online: false, lastChecked: new Date() }
      }));
      addActivity(`${action.name} - Connection failed`, 'error');
    } finally {
      setTestingActionId(null);
    }
  };

  // Handle action click (with confirmation for emergency)
  const handleActionClick = (action: LocationAction) => {
    const config = ACTION_TYPE_CONFIG[action.action_type];
    if (config?.isEmergency || action.action_type === 'SIREN') {
      setActionToConfirm(action);
      setConfirmModalOpen(true);
    } else {
      executeAction(action);
    }
  };

  // Execute the action
  const executeAction = async (action: LocationAction) => {
    try {
      setTriggeringActionId(action.id);
      setConfirmModalOpen(false);
      setActionToConfirm(null);
      
      const result = await locationActionService.triggerAction(action.id);
      
      if (result.dial_info) {
        setDialInfo({
          phoneNumber: result.dial_info.phone_number,
          callerId: result.dial_info.caller_id,
          sipUri: result.dial_info.sip_uri,
          actionName: action.name,
        });
        addActivity(`${action.name} - Call initiated`, 'success');
      } else {
        setSuccessMessage(result.message || `${action.name} triggered successfully`);
        addActivity(`${action.name} - ${result.message || 'Triggered successfully'}`, 'success');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Action failed';
      setError(errorMsg);
      addActivity(`${action.name} - Failed: ${errorMsg}`, 'error');
    } finally {
      setTriggeringActionId(null);
    }
  };

  const handleDialSip = () => {
    if (dialInfo?.sipUri) {
      window.open(dialInfo.sipUri, '_blank');
    } else if (dialInfo?.phoneNumber) {
      window.open(`sip:${dialInfo.phoneNumber}`, '_blank');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard');
  };

  // Categorize actions
  const emergencyActions = configuredActions.filter(a => 
    a.action_type === 'CALL_911' || a.action_type === 'CALL_SECURITY'
  );
  const deviceActions = configuredActions.filter(a => 
    !['CALL_911', 'CALL_SECURITY'].includes(a.action_type)
  );

  if (isLoadingLocations) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" text="Loading locations..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Actions</h1>
            <p className="page-description">Quick controls and emergency response</p>
          </div>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {successMessage && <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />}

        {/* No Location Selected */}
        {!selectedLocationId && (
          <Card padding="lg">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin size={32} className="text-text-muted" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">Select a Location</h3>
              <p className="text-text-muted max-w-md mx-auto">
                Choose a location in the header to view and use the configured actions.
              </p>
            </div>
          </Card>
        )}

        {/* Loading Actions */}
        {selectedLocationId && isLoadingActions && (
          <div className="flex items-center justify-center py-12">
            <Loading size="lg" text="Loading actions..." />
          </div>
        )}

        {/* Actions Content */}
        {selectedLocationId && <EventActionsConsole key={selectedLocationId} locationId={selectedLocationId} />}
        {selectedLocationId && !isLoadingActions && (
          <>
            {/* Location Header */}
            {selectedLocation && (
              <div className="flex items-center gap-2 text-text-muted">
                <MapPin size={16} />
                <span className="text-sm">{selectedLocation.ycl_name}</span>
                <span className="text-text-muted/50">•</span>
                <span className="text-sm">{configuredActions.length} actions configured</span>
              </div>
            )}

            {/* No Actions Configured */}
            {configuredActions.length === 0 && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <p className="text-text-muted mb-4">No actions configured for this location.</p>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/locations/${selectedLocationId}/view`)}
                  >
                    Configure Actions
                  </Button>
                </div>
              </Card>
            )}

            {/* Emergency Actions - Large Buttons */}
            {emergencyActions.length > 0 && (
              <Card padding="lg" className="border-2 border-red-500/20 bg-gradient-to-r from-red-500/5 to-transparent">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle size={20} className="text-red-500" />
                  <h3 className="text-lg font-semibold text-text-primary">Emergency</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {emergencyActions.map(action => {
                    const config = ACTION_TYPE_CONFIG[action.action_type];
                    const isTriggering = triggeringActionId === action.id;
                    const status = actionStatuses[action.id];
                    
                    return (
                      <button
                        key={action.id}
                        onClick={() => handleActionClick(action)}
                        disabled={isTriggering}
                        className={`
                          relative p-8 rounded-xl ${config.bgColor} ${config.hoverColor}
                          text-white font-bold text-xl
                          transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]
                          disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                          shadow-lg hover:shadow-xl
                          flex flex-col items-center justify-center gap-3
                        `}
                      >
                        {isTriggering ? (
                          <Loader2 size={40} className="animate-spin" />
                        ) : (
                          config.iconLarge
                        )}
                        <span>{config.buttonText}</span>
                        <span className="text-sm font-normal opacity-75">{action.name}</span>
                        
                        {/* Status indicator */}
                        {status && (
                          <div className={`absolute top-3 right-3 flex items-center gap-1 text-xs ${status.online ? 'text-green-200' : 'text-red-200'}`}>
                            {status.online ? <Wifi size={12} /> : <WifiOff size={12} />}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Device Controls - Card Grid */}
            {deviceActions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <DoorOpen size={20} className="text-text-muted" />
                  <h3 className="text-lg font-semibold text-text-primary">Device Controls</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {deviceActions.map(action => {
                    const config = ACTION_TYPE_CONFIG[action.action_type];
                    const status = actionStatuses[action.id];
                    const isTriggering = triggeringActionId === action.id;
                    const isTesting = testingActionId === action.id;
                    
                    return (
                      <Card 
                        key={action.id} 
                        padding="md"
                        className="hover:shadow-lg transition-shadow"
                      >
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.bgColor} text-white`}>
                              {config.icon}
                            </div>
                            <div>
                              <h4 className="font-semibold text-text-primary">{action.name}</h4>
                              <p className="text-xs text-text-muted">{config.label}</p>
                            </div>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2 mb-4 text-sm">
                          {status ? (
                            <>
                              {status.online ? (
                                <Wifi size={14} className="text-green-500" />
                              ) : (
                                <WifiOff size={14} className="text-red-500" />
                              )}
                              <span className={status.online ? 'text-green-500' : 'text-red-500'}>
                                {status.online ? 'Online' : 'Offline'}
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 rounded-full bg-yellow-500" />
                              <span className="text-text-muted">Status unknown</span>
                            </>
                          )}
                          <button
                            onClick={() => handleTest(action)}
                            disabled={isTesting}
                            className="ml-auto text-xs text-accent-primary hover:underline disabled:opacity-50"
                          >
                            {isTesting ? 'Testing...' : 'Test'}
                          </button>
                        </div>

                        {/* Trigger Button */}
                        <Button
                          onClick={() => handleActionClick(action)}
                          disabled={isTriggering}
                          className={`w-full ${config.bgColor} ${config.hoverColor} text-white border-0`}
                        >
                          {isTriggering ? (
                            <Loader2 size={18} className="animate-spin mr-2" />
                          ) : (
                            <span className="mr-2">{config.icon}</span>
                          )}
                          {config.buttonText}
                        </Button>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Create Incident Button - Always Available */}
            <Card padding="md">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center bg-indigo-600/10 text-indigo-500">
                    <FileText size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text-primary">Create Incident Report</h4>
                    <p className="text-sm text-text-muted">Log and document an incident</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/events/create', { state: { locationId: selectedLocationId } })}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-0"
                >
                  <FileText size={18} className="mr-2" />
                  Create Incident
                </Button>
              </div>
            </Card>

            {/* Activity Log */}
            {activityLog.length > 0 && (
              <Card padding="md">
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={16} className="text-text-muted" />
                  <h4 className="text-sm font-medium text-text-secondary">Recent Activity</h4>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {activityLog.map(entry => (
                    <div key={entry.id} className="flex items-center gap-3 text-sm">
                      <Clock size={12} className="text-text-muted flex-shrink-0" />
                      <span className="text-text-muted text-xs w-16">
                        {entry.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {entry.type === 'success' && <CheckCircle size={14} className="text-green-500 flex-shrink-0" />}
                      {entry.type === 'error' && <XCircle size={14} className="text-red-500 flex-shrink-0" />}
                      {entry.type === 'info' && <div className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className={`
                        ${entry.type === 'success' ? 'text-green-400' : ''}
                        ${entry.type === 'error' ? 'text-red-400' : ''}
                        ${entry.type === 'info' ? 'text-text-muted' : ''}
                      `}>
                        {entry.message}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Confirmation Modal */}
        <Modal
          isOpen={confirmModalOpen}
          onClose={() => { setConfirmModalOpen(false); setActionToConfirm(null); }}
          title={actionToConfirm?.action_type === 'CALL_911' ? '⚠️ Emergency Call Confirmation' : 'Confirm Action'}
        >
          <div className="space-y-4">
            {actionToConfirm?.action_type === 'CALL_911' ? (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 font-medium">
                  You are about to initiate a 911 emergency call.
                </p>
                <p className="text-text-muted text-sm mt-2">
                  Only proceed if this is a genuine emergency.
                </p>
              </div>
            ) : actionToConfirm?.action_type === 'SIREN' ? (
              <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-orange-400 font-medium">
                  You are about to trigger the siren alarm.
                </p>
                <p className="text-text-muted text-sm mt-2">
                  This will activate the alarm at the location.
                </p>
              </div>
            ) : (
              <p className="text-text-secondary">
                Are you sure you want to execute: <strong>{actionToConfirm?.name}</strong>?
              </p>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setConfirmModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant={actionToConfirm?.action_type === 'CALL_911' ? 'danger' : 'primary'}
                onClick={() => actionToConfirm && executeAction(actionToConfirm)}
              >
                {actionToConfirm?.action_type === 'CALL_911' ? 'Confirm Emergency Call' : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Dial Info Modal */}
        <Modal
          isOpen={!!dialInfo}
          onClose={() => setDialInfo(null)}
          title={`📞 ${dialInfo?.actionName || 'Call'}`}
        >
          {dialInfo && (
            <div className="space-y-4">
              <div className="bg-surface-secondary rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-text-primary mb-2">
                  {dialInfo.phoneNumber}
                </div>
                <div className="text-sm text-text-muted">
                  Caller ID: {dialInfo.callerId}
                </div>
              </div>
              
              <div className="space-y-2">
                <Button onClick={handleDialSip} className="w-full">
                  <Phone size={18} className="mr-2" />
                  Open in Softphone
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="secondary" 
                    onClick={() => copyToClipboard(dialInfo.phoneNumber)}
                  >
                    Copy Number
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => copyToClipboard(dialInfo.callerId)}
                  >
                    Copy Caller ID
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button variant="ghost" onClick={() => setDialInfo(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ActionsPage;
